import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DesignSystemColors from "./DesignSystemColors";
import DesignSystemTypography from "./DesignSystemTypography";
import DesignSystemComponents from "./DesignSystemComponents";
import DesignSystemLayout from "./DesignSystemLayout";
import DesignSystemPreview from "./DesignSystemPreview";
import DesignSystemTemplates from "./DesignSystemTemplates";
import DesignSystemLogoUpload from "./DesignSystemLogoUpload";
import DesignSystemBrandExtract from "./DesignSystemBrandExtract";
import DesignSystemOnboarding from "./DesignSystemOnboarding";
import { DEFAULT_DESIGN_SYSTEM } from "./designSystemDefaults";

const TABS = ["Colors", "Typography", "Components", "Layout", "Templates"];
const TAB_COMPONENTS = { Colors: DesignSystemColors, Typography: DesignSystemTypography, Components: DesignSystemComponents, Layout: DesignSystemLayout };
const TAB_KEYS = { Colors: "colors", Typography: "typography", Components: "components", Layout: "layout" };

export default function DesignSystem() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [showOnboarding, setShowOnboarding] = useState(
        () => !user?.tutorials?.["onboarding-add-brand"]?.completed
    );
    const [activeTab, setActiveTab] = useState("Colors");
    const [ds, setDs] = useState(DEFAULT_DESIGN_SYSTEM);
    const [logo, setLogo] = useState({ url: null });
    const [loaded, setLoaded] = useState(false);
    const { call: getConfig } = useBluelagoon({ method: "getDesignSystem" });
    const { call: saveConfig, isLoading: isSaving } = useBluelagoon({ method: "updateDesignSystem" });
    const { call: analyzeLogoCall } = useBluelagoon({ method: "analyzeLogo" });

    const loadConfig = useCallback(() => {
        getConfig({}).then((res) => {
            if (res?.config) {
                setDs((prev) => ({
                    colors: res.config.colors || prev.colors,
                    typography: res.config.typography || prev.typography,
                    components: res.config.components || prev.components,
                    layout: res.config.layout || prev.layout,
                }));
                if (res.config.logo?.url) setLogo(res.config.logo);
            }
            setLoaded(true);
        });
    }, [getConfig]);

    useEffect(() => { loadConfig(); }, [loadConfig]);

    const updateCategory = (key, val) => setDs((prev) => ({ ...prev, [key]: val }));

    const applyAll = (config) => {
        setDs((prev) => ({
            colors: config.colors || prev.colors,
            typography: config.typography || prev.typography,
            components: config.components || prev.components,
            layout: config.layout || prev.layout,
        }));
    };

    const handleReset = () => { setDs(DEFAULT_DESIGN_SYSTEM); toast.success("Reset to defaults"); };

    const handleLogoChange = async (url) => {
        if (!url) { setLogo({ url: null }); return; }
        setLogo((prev) => ({ ...prev, url }));
        const res = await analyzeLogoCall({ imageUrl: url });
        const analysis = res?.data?.analysis || res?.analysis;
        if (analysis) {
            setLogo({ url, ...analysis });
            toast.success(t("onboarding.designOnboarding.logoAnalyzed"));
        }
    };

    const handleSave = async () => {
        const payload = { ...ds, logo };
        const res = await saveConfig(payload);
        toast[res?.error ? "error" : "success"](res?.error || "Design system saved.");
    };

    if (!loaded) return <div className="p-4 text-muted-foreground">Loading...</div>;

    if (showOnboarding) {
        return (
            <DesignSystemOnboarding
                logoUrl={logo.url}
                onLogoChange={handleLogoChange}
                onBrandExtracted={(config) => {
                    applyAll(config);
                    setShowOnboarding(false);
                }}
                onSkip={() => {
                    setActiveTab("Templates");
                    setShowOnboarding(false);
                }}
            />
        );
    }

    const ActiveComp = TAB_COMPONENTS[activeTab];
    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-semibold">Brand & Design System</h2>
                    <p className="text-sm text-muted-foreground">Configure visual standards for generated apps.</p>
                </div>
                <button onClick={handleSave} disabled={isSaving} className="px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50">
                    {isSaving ? "Saving..." : "Save Changes"}
                </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DesignSystemLogoUpload logoUrl={logo.url} onLogoChange={handleLogoChange} />
                <DesignSystemBrandExtract onDesignExtracted={applyAll} />
            </div>
            <div className="flex gap-2 border-b border-input">
                {TABS.map((tab) => (
                    <button key={tab} onClick={() => setActiveTab(tab)} className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        {tab}
                    </button>
                ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                    {ActiveComp && <ActiveComp {...{ [TAB_KEYS[activeTab]]: ds[TAB_KEYS[activeTab]] }} onChange={(v) => updateCategory(TAB_KEYS[activeTab], v)} />}
                    {activeTab === "Templates" && <DesignSystemTemplates onSelect={(t) => { applyAll(t); toast.success(`Applied "${t.name}"`); }} onReset={handleReset} />}
                </div>
                <DesignSystemPreview colors={ds.colors} typography={ds.typography} components={ds.components} />
            </div>
        </div>
    );
}
